// ---------------------------------------------------------------------------
// Dash creative-types API client + form utilities — feature #85 (OPUS-D17).
//
// Mirrors the HTTP contract from `apps/api/src/routes/creative-types.ts`:
//
//   GET    /api/v1/creative-types          -> { types: [...], platform_codes }
//   POST   /api/v1/creative-types          -> { type } (201) | { error }
//   PATCH  /api/v1/creative-types/:code    -> { type }       | { error }
//   DELETE /api/v1/creative-types/:code    -> { deleted: true, code }
//   POST   /api/v1/creative-types/validate -> { ok, reason? }
//
// Owned by OPUS-D17. Only this file + `./TypesList.tsx` +
// `../../routes/creatives.types.tsx` are in the agent's lane.
//
// Implementation notes
// --------------------
// The dash scaffold ships with @tanstack/react-query + sonner but NO
// react-hook-form / zod dependency, and this agent cannot edit
// apps/dash/package.json. To still deliver the "React Hook Form + Zod4
// modal" semantic required by the brief, this file exports:
//
//   - A tiny Zod4-style validator (`types.Schema.parse`) returning
//     { ok, data } | { ok: false, issues } the way zod/v4 would, with
//     field-level error paths.
//   - A minimal `useTypedForm` hook mimicking react-hook-form's
//     `register + handleSubmit + errors` surface for the create/override
//     modal used by the route file.
//
// Keep this file tree-shakable and side-effect free (only hooks run
// imperatively on use).
// ---------------------------------------------------------------------------

import {
	useCallback,
	useRef,
	useState,
	type ChangeEvent,
	type FormEvent,
} from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseQueryResult,
} from "@tanstack/react-query";

// ---------------------------------------------------------------------------
// Domain types — kept in sync with the API's rowToView / mergeCatalog output.
// ---------------------------------------------------------------------------

export type CreativeTypeSource = "platform" | "tenant";

export interface CreativeTypeView {
	code: string;
	label: string;
	allowed_mimes: string[];
	max_size_bytes: number | null;
	source: CreativeTypeSource;
	shadows_platform?: boolean;
}

export interface TypesListResponse {
	types: CreativeTypeView[];
	platform_codes: string[];
}

export type ValidateResult =
	| { ok: true; code: string; source: CreativeTypeSource }
	| {
			ok: false;
			reason: string;
			code?: string;
			allowed_mimes?: string[];
			max_size_bytes?: number | null;
	  };

// ---------------------------------------------------------------------------
// Config — base URL + tenant header. Vite injects VITE_API_URL /
// VITE_TENANT_ID. Defaults are aligned with the local smoke port (8787).
// ---------------------------------------------------------------------------

function getApiBase(): string {
	const env = (
		typeof import.meta !== "undefined"
			? (import.meta as unknown as { env?: Record<string, string | undefined> }).env
			: undefined
	) as Record<string, string | undefined> | undefined;
	const raw = env?.VITE_API_URL ?? "http://localhost:8787";
	return raw.replace(/\/+$/, "");
}

function getTenantId(): string {
	const env = (
		typeof import.meta !== "undefined"
			? (import.meta as unknown as { env?: Record<string, string | undefined> }).env
			: undefined
	) as Record<string, string | undefined> | undefined;
	return env?.VITE_TENANT_ID ?? "demo";
}

function typesUrl(suffix = ""): string {
	return `${getApiBase()}/api/v1/creative-types${suffix}`;
}

// ---------------------------------------------------------------------------
// Tiny shared fetch wrapper — returns parsed JSON, throws ApiError on !ok.
// ---------------------------------------------------------------------------

export class ApiError extends Error {
	readonly status: number;
	readonly body: unknown;
	constructor(status: number, message: string, body: unknown) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.body = body;
	}
}

async function request<T>(input: string, init: RequestInit = {}): Promise<T> {
	const headers: Record<string, string> = {
		accept: "application/json",
		"x-tenant-id": getTenantId(),
		...(init.body !== undefined ? { "content-type": "application/json" } : {}),
		...((init.headers as Record<string, string> | undefined) ?? {}),
	};
	const res = await fetch(input, { ...init, headers });
	const contentType = res.headers.get("content-type") ?? "";
	const body: unknown = contentType.includes("application/json")
		? await res.json().catch(() => null)
		: await res.text().catch(() => null);
	if (!res.ok) {
		const extracted =
			body &&
			typeof body === "object" &&
			"error" in body &&
			typeof (body as { error?: unknown }).error === "string"
				? (body as { error: string }).error
				: null;
		throw new ApiError(res.status, extracted ?? `HTTP ${res.status}`, body);
	}
	return body as T;
}

// ---------------------------------------------------------------------------
// Fetchers — used by hooks + direct imports.
// ---------------------------------------------------------------------------

export async function fetchCreativeTypes(init?: RequestInit): Promise<TypesListResponse> {
	return request<TypesListResponse>(typesUrl(""), { method: "GET", ...init });
}

export interface CreateTypePayload {
	code: string;
	label: string;
	allowedMimes: string[];
	maxSizeBytes: number | null;
}

export async function createCreativeType(payload: CreateTypePayload): Promise<CreativeTypeView> {
	const res = await request<{ type: CreativeTypeView }>(typesUrl(""), {
		method: "POST",
		body: JSON.stringify(payload),
	});
	return res.type;
}

export interface UpdateTypePayload {
	code: string;
	label?: string;
	allowedMimes?: string[];
	maxSizeBytes?: number | null;
}

export async function updateCreativeType(payload: UpdateTypePayload): Promise<CreativeTypeView> {
	const { code, ...body } = payload;
	const res = await request<{ type: CreativeTypeView }>(
		typesUrl(`/${encodeURIComponent(code)}`),
		{ method: "PATCH", body: JSON.stringify(body) },
	);
	return res.type;
}

export async function deleteCreativeType(code: string): Promise<{ deleted: true; code: string }> {
	return request<{ deleted: true; code: string }>(
		typesUrl(`/${encodeURIComponent(code)}`),
		{ method: "DELETE" },
	);
}

export interface ValidatePayload {
	code: string;
	mime: string;
	sizeBytes?: number | null;
}

export async function validateCreative(payload: ValidatePayload): Promise<ValidateResult> {
	try {
		return await request<ValidateResult>(typesUrl("/validate"), {
			method: "POST",
			body: JSON.stringify(payload),
		});
	} catch (err) {
		// The API returns 400/404 with an { ok: false, reason } body on some
		// failures. Re-surface that instead of blowing up the mutation.
		if (err instanceof ApiError && err.body && typeof err.body === "object") {
			const body = err.body as { ok?: unknown; reason?: unknown };
			if (body.ok === false && typeof body.reason === "string") {
				return body as ValidateResult;
			}
		}
		throw err;
	}
}

// ---------------------------------------------------------------------------
// Helpers — pure, used for rendering and form init.
// ---------------------------------------------------------------------------

export function formatBytes(n: number | null | undefined): string {
	if (n === null || n === undefined) return "no limit";
	if (!Number.isFinite(n) || n < 0) return "—";
	if (n === 0) return "0 B";
	const units = ["B", "KB", "MB", "GB", "TB"];
	const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
	const v = n / 1024 ** i;
	const fixed = v >= 10 || i === 0 ? v.toFixed(0) : v.toFixed(1);
	return `${fixed} ${units[i]}`;
}

// Reverse — parse "5 MB" / "500kb" / "1024" into bytes. Returns null on
// empty, NaN on invalid. Used by the form to accept human-readable input
// before POSTing the canonical byte count expected by the API.
export function parseBytes(input: string): number | null | typeof Number.NaN {
	const trimmed = input.trim();
	if (trimmed === "") return null;
	const match = trimmed.match(/^(\d+(?:\.\d+)?)\s*(b|kb|mb|gb|tb)?$/i);
	if (!match) return Number.NaN;
	const value = Number.parseFloat(match[1]);
	if (!Number.isFinite(value)) return Number.NaN;
	const unit = (match[2] ?? "b").toLowerCase();
	const multipliers: Record<string, number> = {
		b: 1,
		kb: 1024,
		mb: 1024 ** 2,
		gb: 1024 ** 3,
		tb: 1024 ** 4,
	};
	const bytes = Math.round(value * (multipliers[unit] ?? 1));
	if (!Number.isInteger(bytes) || bytes < 0) return Number.NaN;
	return bytes;
}

// Parse a comma / whitespace separated string into a canonical mime list.
export function parseMimeList(input: string): string[] {
	return input
		.split(/[\s,]+/)
		.map((s) => s.trim().toLowerCase())
		.filter((s) => s.length > 0)
		.filter((s, i, arr) => arr.indexOf(s) === i);
}

// ---------------------------------------------------------------------------
// Zod4-style schema for the create/override modal. Hand-rolled to avoid a
// `zod` dependency (agent cannot edit package.json). Surface:
//
//     const r = TypeFormSchema.parse(raw);
//     r.ok ? r.data : r.issues; // { path, message }[]
//
// The API does its own validation — this one is for UX feedback.
// ---------------------------------------------------------------------------

const CODE_RE = /^[a-z][a-z0-9_-]{1,47}$/;
const MIME_RE = /^[a-z0-9][a-z0-9!#$&^_.+-]{0,126}\/[a-z0-9!#$&^_.+*-]{1,126}$/;

export type SchemaIssue = { path: string; message: string };

export interface ParsedTypeForm {
	code: string;
	label: string;
	allowedMimes: string[];
	maxSizeBytes: number | null;
}

export interface RawTypeForm {
	code: string;
	label: string;
	allowedMimesRaw: string;
	maxSizeRaw: string;
}

export const TypeFormSchema = {
	parse(
		raw: RawTypeForm,
		opts: { requireCode?: boolean } = { requireCode: true },
	): { ok: true; data: ParsedTypeForm } | { ok: false; issues: SchemaIssue[] } {
		const issues: SchemaIssue[] = [];

		const code = raw.code.trim().toLowerCase();
		if (opts.requireCode !== false) {
			if (code.length === 0) {
				issues.push({ path: "code", message: "Code is required." });
			} else if (!CODE_RE.test(code)) {
				issues.push({
					path: "code",
					message: "Use 2–48 chars: a–z, 0–9, dash, underscore. Must start with a letter.",
				});
			}
		}

		const label = raw.label.trim();
		if (label.length === 0) {
			issues.push({ path: "label", message: "Label is required." });
		} else if (label.length > 128) {
			issues.push({ path: "label", message: "Label must be 128 chars or fewer." });
		}

		const mimes = parseMimeList(raw.allowedMimesRaw);
		if (mimes.length === 0) {
			issues.push({
				path: "allowedMimes",
				message: "At least one MIME type is required.",
			});
		} else if (mimes.length > 64) {
			issues.push({
				path: "allowedMimes",
				message: "64 MIME types maximum.",
			});
		} else {
			const bad = mimes.filter((m) => m !== "*/*" && !MIME_RE.test(m));
			if (bad.length > 0) {
				issues.push({
					path: "allowedMimes",
					message: `Invalid MIME: ${bad.slice(0, 3).join(", ")}${bad.length > 3 ? "…" : ""}`,
				});
			}
		}

		let maxSizeBytes: number | null = null;
		const parsedMax = parseBytes(raw.maxSizeRaw);
		if (Number.isNaN(parsedMax)) {
			issues.push({
				path: "maxSizeBytes",
				message: "Use bytes or a unit suffix: 500KB, 2MB, 1GB.",
			});
		} else {
			maxSizeBytes = parsedMax as number | null;
			if (typeof maxSizeBytes === "number" && maxSizeBytes > 5 * 1024 ** 3) {
				issues.push({
					path: "maxSizeBytes",
					message: "Max 5 GiB.",
				});
			}
		}

		if (issues.length > 0) return { ok: false, issues };
		return {
			ok: true,
			data: {
				code,
				label,
				allowedMimes: mimes,
				maxSizeBytes,
			},
		};
	},
} as const;

// ---------------------------------------------------------------------------
// React-Hook-Form-style controlled form hook. Mirrors RHF's core API
// (register / handleSubmit / formState.errors / reset / setValue) so the
// route file reads like an RHF form.
// ---------------------------------------------------------------------------

export interface TypedFormFieldRegistration {
	name: keyof RawTypeForm;
	value: string;
	onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
	onBlur: () => void;
	"aria-invalid": boolean;
	"aria-describedby"?: string;
	id: string;
}

export interface TypedFormState {
	errors: Partial<Record<keyof RawTypeForm, string>>;
	isSubmitting: boolean;
	isDirty: boolean;
}

export interface UseTypedFormReturn {
	values: RawTypeForm;
	register: (name: keyof RawTypeForm) => TypedFormFieldRegistration;
	handleSubmit: (
		onValid: (data: ParsedTypeForm) => void | Promise<void>,
	) => (e: FormEvent<HTMLFormElement>) => Promise<void>;
	reset: (next?: Partial<RawTypeForm>) => void;
	setValue: (name: keyof RawTypeForm, value: string) => void;
	formState: TypedFormState;
	setSubmitting: (next: boolean) => void;
}

const EMPTY_FORM: RawTypeForm = {
	code: "",
	label: "",
	allowedMimesRaw: "",
	maxSizeRaw: "",
};

export function typeViewToRaw(t: CreativeTypeView): RawTypeForm {
	return {
		code: t.code,
		label: t.label,
		allowedMimesRaw: t.allowed_mimes.join(", "),
		maxSizeRaw: t.max_size_bytes === null ? "" : String(t.max_size_bytes),
	};
}

export function useTypedForm(
	initial: RawTypeForm = EMPTY_FORM,
	opts: { requireCode?: boolean } = {},
): UseTypedFormReturn {
	const [values, setValues] = useState<RawTypeForm>(initial);
	const [errors, setErrors] = useState<TypedFormState["errors"]>({});
	const [isSubmitting, setSubmitting] = useState(false);
	const dirtyRef = useRef(false);
	const initialRef = useRef(initial);

	const setValue = useCallback((name: keyof RawTypeForm, value: string) => {
		dirtyRef.current = true;
		setValues((prev) => ({ ...prev, [name]: value }));
		setErrors((prev) => {
			if (!prev[name]) return prev;
			const next = { ...prev };
			delete next[name];
			return next;
		});
	}, []);

	const register = useCallback(
		(name: keyof RawTypeForm): TypedFormFieldRegistration => ({
			name,
			value: values[name],
			id: `field-${name}`,
			onChange: (e) => setValue(name, e.target.value),
			onBlur: () => {},
			"aria-invalid": Boolean(errors[name]),
			"aria-describedby": errors[name] ? `err-${name}` : undefined,
		}),
		[values, errors, setValue],
	);

	const handleSubmit = useCallback(
		(onValid: (data: ParsedTypeForm) => void | Promise<void>) =>
			async (e: FormEvent<HTMLFormElement>) => {
				e.preventDefault();
				const result = TypeFormSchema.parse(values, {
					requireCode: opts.requireCode !== false,
				});
				if (!result.ok) {
					const next: TypedFormState["errors"] = {};
					for (const issue of result.issues) {
						// Map schema paths (semantic) onto RHF-style field names (raw).
						if (issue.path === "allowedMimes") {
							next.allowedMimesRaw = issue.message;
						} else if (issue.path === "maxSizeBytes") {
							next.maxSizeRaw = issue.message;
						} else if (issue.path === "code" || issue.path === "label") {
							next[issue.path] = issue.message;
						}
					}
					setErrors(next);
					return;
				}
				setErrors({});
				setSubmitting(true);
				try {
					await onValid(result.data);
				} finally {
					setSubmitting(false);
				}
			},
		[values, opts.requireCode],
	);

	const reset = useCallback((next?: Partial<RawTypeForm>) => {
		const merged: RawTypeForm = { ...EMPTY_FORM, ...initialRef.current, ...next };
		initialRef.current = merged;
		setValues(merged);
		setErrors({});
		dirtyRef.current = false;
	}, []);

	return {
		values,
		register,
		handleSubmit,
		reset,
		setValue,
		formState: {
			errors,
			isSubmitting,
			isDirty: dirtyRef.current,
		},
		setSubmitting,
	};
}

// ---------------------------------------------------------------------------
// TanStack Query v5 hooks.
// ---------------------------------------------------------------------------

export const CREATIVE_TYPES_KEY = ["creative-types"] as const;

export function useCreativeTypes(): UseQueryResult<TypesListResponse, ApiError> {
	return useQuery<TypesListResponse, ApiError>({
		queryKey: CREATIVE_TYPES_KEY,
		queryFn: () => fetchCreativeTypes(),
		staleTime: 30_000,
		refetchOnWindowFocus: false,
		retry: 1,
	});
}

export function useCreateCreativeType() {
	const qc = useQueryClient();
	return useMutation<CreativeTypeView, ApiError, CreateTypePayload>({
		mutationFn: createCreativeType,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: CREATIVE_TYPES_KEY });
		},
	});
}

export function useUpdateCreativeType() {
	const qc = useQueryClient();
	return useMutation<CreativeTypeView, ApiError, UpdateTypePayload>({
		mutationFn: updateCreativeType,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: CREATIVE_TYPES_KEY });
		},
	});
}

export function useDeleteCreativeType() {
	const qc = useQueryClient();
	return useMutation<{ deleted: true; code: string }, ApiError, string>({
		mutationFn: deleteCreativeType,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: CREATIVE_TYPES_KEY });
		},
	});
}

export function useValidateCreative() {
	return useMutation<ValidateResult, ApiError, ValidatePayload>({
		mutationFn: validateCreative,
	});
}

// ---------------------------------------------------------------------------
// View-model helpers — used by the list + route for rendering.
// ---------------------------------------------------------------------------

export function sortTypesByCode(items: CreativeTypeView[]): CreativeTypeView[] {
	return [...items].sort((a, b) => a.code.localeCompare(b.code));
}

export function countOverrides(items: CreativeTypeView[]): number {
	return items.reduce((acc, t) => acc + (t.source === "tenant" ? 1 : 0), 0);
}

export function humanizeValidateReason(reason: string): string {
	const map: Record<string, string> = {
		mime_required: "Please enter a MIME type.",
		mime_not_allowed: "MIME type is not allowed for this creative type.",
		size_invalid: "Size must be a non-negative whole number of bytes.",
		size_exceeds_max: "File size is larger than the allowed maximum.",
		unknown_creative_type: "Unknown creative type.",
		code_required: "Creative type code is required.",
		code_invalid: "Invalid creative type code.",
		label_required: "Label is required.",
		label_too_long: "Label is too long.",
		allowed_mimes_shape: "allowed_mimes must be an array.",
		allowed_mimes_empty: "At least one allowed MIME type is required.",
		allowed_mimes_too_many: "Too many MIME types (max 64).",
		allowed_mimes_invalid: "One or more MIME types are invalid.",
		max_size_bytes_invalid: "Invalid max size value.",
		max_size_bytes_negative: "Max size cannot be negative.",
		max_size_bytes_too_large: "Max size exceeds the 5 GiB ceiling.",
		creative_type_exists: "A creative type with this code already exists.",
		not_found: "Creative type not found.",
		tenant_required: "Tenant context is required.",
	};
	return map[reason] ?? reason;
}

// ---------------------------------------------------------------------------
// TypesList / route shared computed shape used in tests + the page header.
// ---------------------------------------------------------------------------

export interface CatalogSummary {
	total: number;
	platform: number;
	tenant: number;
	shadowed: number;
}

export function summarizeCatalog(items: CreativeTypeView[]): CatalogSummary {
	let platform = 0;
	let tenant = 0;
	let shadowed = 0;
	for (const t of items) {
		if (t.source === "tenant") {
			tenant += 1;
			if (t.shadows_platform) shadowed += 1;
		} else {
			platform += 1;
		}
	}
	return { total: items.length, platform, tenant, shadowed };
}

export const __internals = { getApiBase, getTenantId, typesUrl };

// Re-export react types that the route file needs so it can import everything
// from this single module (keeps the 3-file budget tight).
export type { ChangeEvent, FormEvent } from "react";
